'use client';

import { useRef, useState } from 'react';
import { DateTime } from 'luxon';

import { cn } from '@poseidon/components/cn';
import { Button, DateBoxButton, Textarea } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';
import { Communication } from '@poseidon/components/StrokeIcons';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { api } from '@/utils/api';

type OnExpire = 'vanish' | 'shift_week';

export interface ScheduleCallButtonProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  /** Called after a call is scheduled — e.g. to invalidate the calls queue / reminder caches. */
  onScheduled?: () => void;
  className?: string;
}

/** A DATE tile that opens the native date picker; shows the picked day or a placeholder. */
function DateTile({
  label,
  valueMs,
  minMs,
  onChange,
}: {
  label: string;
  valueMs: number | null;
  minMs?: number;
  onChange: (ms: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dt = valueMs != null ? DateTime.fromMillis(valueMs) : null;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <DateBoxButton
        size="lg"
        month={dt != null ? dt.toFormat('LLL').toUpperCase() : '•••'}
        day={dt != null ? dt.toFormat('d') : '?'}
        tooltip={`Pick the ${label.toLowerCase()}`}
        onClick={() => inputRef.current?.showPicker()}>
        <input
          ref={inputRef}
          type="date"
          className="absolute inset-0 cursor-pointer opacity-0"
          value={dt != null ? (dt.toISODate() ?? '') : ''}
          min={minMs != null ? (DateTime.fromMillis(minMs).toISODate() ?? undefined) : undefined}
          onChange={(e) => {
            const v = e.target.value;
            if (v) onChange(DateTime.fromISO(v).toMillis());
          }}
        />
      </DateBoxButton>
    </div>
  );
}

/**
 * Schedule a call on a contact for the future — no campaign required. Opens a small popup to pick a
 * start → end date window and a "when the window lapses" disposition (vanish, or shift to next
 * week), then creates a standalone scheduled call that surfaces in the `/todo/calls` queue on its
 * start date. Gate the render on `voiceCallingEnabled` at the call site.
 */
export function ScheduleCallButton({ teamId, contactId, contactName, onScheduled, className }: ScheduleCallButtonProps) {
  const [open, setOpen] = useState(false);
  // Default to a today → +2 days window (a 3-day "sometime this week" window).
  const [startMs, setStartMs] = useState<number>(() => DateTime.now().startOf('day').toMillis());
  const [endMs, setEndMs] = useState<number>(() => DateTime.now().plus({ days: 2 }).startOf('day').toMillis());
  const [onExpire, setOnExpire] = useState<OnExpire>('vanish');
  const [note, setNote] = useState('');

  const utils = api.useUtils();
  const scheduleCallMutation = api.voiceCall.scheduleCall.useMutation();

  const invalid = endMs < startMs;

  const handleSave = async () => {
    if (invalid) return;
    await updateWithToast({
      loading: 'Scheduling call…',
      success: 'Call scheduled',
      // Store absolute instants: window START = start-of-day, END = end-of-day, in the rep's tz.
      mutationFnAsync: () =>
        scheduleCallMutation.mutateAsync({
          teamId,
          contactId,
          planRunAt: DateTime.fromMillis(startMs).startOf('day').toMillis(),
          planRunBy: DateTime.fromMillis(endMs).endOf('day').toMillis(),
          onExpire,
          notes: note.trim() ? note.trim() : undefined,
        }),
    });
    void utils.voiceCall.callQueue.invalidate();
    void utils.contactDetailBootstrap.forContact.invalidate({ teamId, contactId });
    setOpen(false);
    setNote('');
    onScheduled?.();
  };

  return (
    <>
      {/* Full-width row (sits below the reminder card); left-aligned to match the other action rows. */}
      <Button
        color="blue"
        tone="soft"
        icon={<Communication.Phone />}
        tooltip="Schedule a call for later"
        className={cn('w-full justify-start', className)}
        onClick={() => setOpen(true)}>
        Schedule call
      </Button>

      {open && (
        <Popup onClose={() => setOpen(false)} className="w-[22rem] max-w-full">
          <div className="flex flex-col gap-4 p-1">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Schedule a call</h3>
              {contactName != null && (
                <p className="text-sm text-gray-500 dark:text-gray-400">with {contactName}</p>
              )}
            </div>

            <div className="flex items-end justify-center gap-4">
              <DateTile label="Start" valueMs={startMs} onChange={setStartMs} />
              <span className="pb-4 text-gray-400">→</span>
              <DateTile label="End" valueMs={endMs} minMs={startMs} onChange={setEndMs} />
            </div>
            {invalid && (
              <p className="text-center text-xs text-red-500">The end date can’t be before the start date.</p>
            )}

            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">If the call isn’t made by the end date</span>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  color="blue"
                  tone={onExpire === 'vanish' ? 'filled' : 'outline'}
                  onClick={() => setOnExpire('vanish')}>
                  Drop it
                </Button>
                <Button
                  color="blue"
                  tone={onExpire === 'shift_week' ? 'filled' : 'outline'}
                  onClick={() => setOnExpire('shift_week')}>
                  Try next week
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">Note (optional)</span>
              <Textarea
                placeholder="Why call, what to mention…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button tone="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button color="blue" disabled={invalid || scheduleCallMutation.isPending} onClick={handleSave}>
                Schedule
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
}
